/**
 * Popup Manager
 * 统一管理所有弹窗的创建、显示、隐藏和销毁
 */

class PopupManager {
  constructor() {
    this.popups = new Map();
    this.queue = [];
    this.isProcessing = false;
    this.container = null;
    this.init();
  }

  init() {
    this.container = document.createElement('div');
    this.container.className = 'popup-manager-container';
    document.body.appendChild(this.container);
  }

  /**
   * 创建弹窗
   * @param {string} id - 弹窗唯一标识符
   * @param {Object} options - 弹窗配置选项
   * @returns {string} - 创建的弹窗ID
   */
  create(id, options = {}) {
    const defaultOptions = {
      type: 'info', // info, animation, media
      title: '',
      content: '',
      buttons: [],
      autoClose: false,
      closeOnClickOutside: true,
      priority: 0,
      delay: 0,
      onClose: null,
      onShow: null,
      mask: true, // Show mask layer by default
      width: '350px', // Popup width
      height: 'auto', // Popup height
      titleAlign: 'left', // Title alignment: left, center
      showDivider: false // Whether to show divider between title and content
    };

    const popupOptions = { ...defaultOptions, ...options };
    this.popups.set(id, popupOptions);

    return id;
  }

  /**
   * 显示弹窗
   * @param {string} id - 弹窗ID
   * @param {Object} options - 可选的临时配置
   */
  show(id, options = {}) {
    const popup = this.popups.get(id);
    if (!popup) {
      console.warn(`Popup ${id} not found`);
      return;
    }

    const popupOptions = { ...popup, ...options };
    
    if (popupOptions.delay > 0) {
      setTimeout(() => this._showPopup(id, popupOptions), popupOptions.delay);
    } else {
      this._showPopup(id, popupOptions);
    }
  }

  /**
   * 隐藏弹窗
   * @param {string} id - 弹窗ID
   */
  hide(id) {
    const popupElement = document.getElementById(`popup-${id}`);
    if (popupElement) {
      popupElement.classList.add('popup-hidden');
      setTimeout(() => {
        popupElement.remove();
        
        // Remove mask layer if no more popups are shown
        const remainingPopups = this.container.querySelectorAll('.popup:not(.popup-hidden)');
        if (remainingPopups.length === 0) {
          this.container.classList.remove('show-mask');
        }
      }, 300);
    }
  }

  /**
   * 销毁弹窗
   * @param {string} id - 弹窗ID
   */
  destroy(id) {
    this.hide(id);
    this.popups.delete(id);
  }

  /**
   * 隐藏所有弹窗
   */
  hideAll() {
    document.querySelectorAll('.popup').forEach(element => {
      element.remove();
    });
    this.queue = [];
    this.isProcessing = false;
  }

  /**
   * 显示信息弹窗
   * @param {Object} options - 弹窗配置
   * @returns {string} - 弹窗ID
   */
  showInfo(options = {}) {
    const id = `info-${Date.now()}`;
    this.create(id, { ...options, type: 'info' });
    this.show(id);
    return id;
  }

  /**
   * 显示动画弹窗
   * @param {Object} options - 弹窗配置
   * @returns {string} - 弹窗ID
   */
  showAnimation(options = {}) {
    const id = `animation-${Date.now()}`;
    this.create(id, { ...options, type: 'animation' });
    this.show(id);
    return id;
  }

  /**
   * 显示媒体弹窗
   * @param {Object} options - 弹窗配置
   * @returns {string} - 弹窗ID
   */
  showMedia(options = {}) {
    const id = `media-${Date.now()}`;
    this.create(id, { ...options, type: 'media' });
    this.show(id);
    return id;
  }

  /**
   * 内部方法：显示弹窗
   * @private
   */
  _showPopup(id, options) {
    const popupElement = this._createPopupElement(id, options);
    
    if (!popupElement) return;

    // Show/hide mask layer based on options
    if (options.mask) {
      this.container.classList.add('show-mask');
    } else {
      this.container.classList.remove('show-mask');
    }

    this.container.appendChild(popupElement);

    // 添加关闭事件
    const closeButton = popupElement.querySelector('.popup__header-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide(id));
    }

    // 添加按钮事件
    const buttons = popupElement.querySelectorAll('.popup__button');
    buttons.forEach((button, index) => {
      button.addEventListener('click', () => {
        const buttonAction = options.buttons[index]?.action;
        console.log(buttonAction);
        if (typeof buttonAction === 'function') {
          buttonAction();
        }
        this.hide(id);
      });
    });

    // 点击外部关闭
    if (options.closeOnClickOutside) {
      setTimeout(() => {
        document.addEventListener('click', (e) => {
          if (!popupElement.contains(e.target)) {
            this.hide(id);
          }
        });
      }, 100);
    }

    // 自动关闭
    if (options.autoClose) {
      setTimeout(() => this.hide(id), options.autoClose);
    }

    // 触发onShow回调
    if (typeof options.onShow === 'function') {
      options.onShow();
    }
  }

  /**
   * 内部方法：创建弹窗DOM元素
   * @private
   */
  _createPopupElement(id, options) {
    // Get template from preloaded templates
    const templateElement = document.querySelector(`.popup-templates .popup--${options.type}`);
    
    if (!templateElement) {
      console.warn(`Template for popup type ${options.type} not found`);
      return null;
    }

    // Clone template
    const popupElement = templateElement.cloneNode(true);
    popupElement.id = `popup-${id}`;
    
    // Fill in content
    this._fillPopupContent(popupElement, options);
    
    return popupElement;
  }

  /**
   * 填充弹窗内容
   * @private
   */
  _fillPopupContent(popupElement, options) {
    // Set popup width and height
    popupElement.style.width = options.width || '350px';
    popupElement.style.height = options.height || 'auto';

    // Set title
    const titleElement = popupElement.querySelector('.popup__header-title');
    if (titleElement) {
      titleElement.textContent = options.title || '';
      titleElement.style.textAlign = options.titleAlign || 'left';
    }

    // Set divider visibility
    const dividerElement = popupElement.querySelector('.popup__divider');
    if (dividerElement) {
      dividerElement.style.display = options.showDivider ? 'block' : 'none';
    }

    // Set content
    const contentElement = popupElement.querySelector('.popup__content');
    if (contentElement) {
      contentElement.innerHTML = options.content || '';
    }

    // Set media content
    if (options.type === 'media' && options.mediaUrl) {
      const mediaContainer = popupElement.querySelector('.popup__media-container');
      const mediaCaption = popupElement.querySelector('.popup__media-caption');
      
      if (mediaContainer) {
        const isVideo = options.mediaUrl.match(/\.(mp4|webm|ogg)$/i);
        if (isVideo) {
          mediaContainer.innerHTML = `
            <video class="popup__media" src="${options.mediaUrl}" controls>
              Your browser does not support the video tag.
            </video>
          `;
        } else {
          mediaContainer.innerHTML = `
            <img class="popup__media" src="${options.mediaUrl}" alt="${options.mediaAlt || 'Popup media'}">
          `;
        }
      }
      
      if (mediaCaption && options.content) {
        mediaCaption.innerHTML = options.content;
      }
    }

    // Set buttons
    const buttonsContainer = popupElement.querySelector('.popup__buttons');
    if (buttonsContainer) {
      if (options.buttons && options.buttons.length > 0) {
        buttonsContainer.innerHTML = options.buttons.map(button => 
          `<button class="popup__button">${button.text}</button>`
        ).join('');
      } else {
        buttonsContainer.style.display = 'none';
      }
    }
  }
}

// 创建单例实例
const popupManager = new PopupManager();

// 导出到全局
window.PopupManager = popupManager;